import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

import { cn } from "@/lib/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-semibold transition-colors duration-fast " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 " +
    "disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-primary-500 text-white shadow-elev-1 hover:bg-primary-600 active:bg-primary-700",
        secondary:
          "bg-surface text-ink-700 border border-border-strong hover:bg-primary-50 dark:hover:bg-primary-900/20",
        ghost:
          "bg-transparent text-ink-700 hover:bg-ink-300/20",
        destructive:
          "bg-danger-500 text-white hover:opacity-90",
        link: "text-primary-500 underline-offset-4 hover:underline",
        // Skeumorphic raised variants — match the topbar/sidebar design language
        skeu:
          "group rounded-xl bg-gradient-to-b from-white to-slate-50 border border-black/[0.08] " +
          "shadow-[0_1px_4px_rgba(0,0,0,0.10),inset_0_1px_0_rgba(255,255,255,1)] " +
          "hover:-translate-y-0.5 hover:shadow-[0_3px_8px_rgba(0,0,0,0.13),inset_0_1px_0_rgba(255,255,255,1)] " +
          "text-ink-700 transition-all duration-200 " +
          "dark:bg-white/[0.07] dark:bg-none dark:border-white/[0.12] dark:text-ink-200 " +
          "dark:shadow-[0_1px_4px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.08)] " +
          "dark:hover:shadow-[0_3px_8px_rgba(0,0,0,0.5)]",
        "skeu-primary":
          "group rounded-xl bg-gradient-to-b from-primary-500 to-primary-600 border border-primary-700/30 " +
          "shadow-[0_1px_4px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.25)] " +
          "hover:-translate-y-0.5 hover:shadow-[0_3px_10px_rgb(var(--color-primary-500)/0.45),inset_0_1px_0_rgba(255,255,255,0.25)] " +
          "text-white font-semibold transition-all duration-200",
      },
      size: {
        // `max-md:` rules give phones a 44px tap target; desktop sizes are unchanged.
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm max-md:h-11",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 max-md:h-11 max-md:w-11",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
